import ProducerPage from "@bottlebooks/bottlebooks-site-base/src/components/ProducerPage/ProducerPage.next";
import { LoaderFunctionArgs, useLoaderData, useParams } from "react-router-dom";
import { z } from "zod";
import { bbCollectionIdSchema } from "~/bbCollectionIdSchema";
import { graphql } from "~/gql";
import { getFragment } from "~/gql-local/fragment-masking";
import graphQLClient from "~/graphQLClient";

const paramsSchema = z.object({
  locale: z.enum(["en", "de", "es", "fr"]),
  collectionId: z.string(),
  producerId: z.string(),
});

const fragment = graphql(/* GraphQL */ `
  fragment ProducerPage_Event on Event {
    eventId
    registeredBrands(filter: { brandId: { eq: $producerId } }) {
      nodes {
        __typename
        # stand {
        #   name
        #   roomName
        # }
        # registrationId
        # regionSummary
        # priceSummary
        # priceRangeSummary
        # primaryVarietySummary
        registrationId

        companyId
        producerId: brandId
        products {
          nodes {
            productId
            exhibitorId: companyId
            producerId
            # ...ProducerPage_Product
          }
        }
        registration {
          companyId
          company {
            name
          }
        }
        ...RegisteredBrand_ProducerPage
        profile {
          ...BrandAddressSection_Profile
          ...BrandDetailsSection_Profile
          ...BrandLogo
          ...BrandHeader
          ...BrandBanner
          name
          sortName
          country
          countryName: country(format: LOCALIZED)
          countryCode: country(format: RAW)
          region
          city
          distributionInCountries
          seekingDistributionInCountries
        }
      }
    }
  }
`);

type LoaderData = Awaited<ReturnType<typeof Loader>>;
export async function Loader({ params }: LoaderFunctionArgs) {
  const {
    locale,
    collectionId: collectionIdSegment,
    producerId,
  } = paramsSchema.parse(params);
  const collectionId = bbCollectionIdSchema.parse(collectionIdSegment);
  const result = await graphQLClient.request(
    graphql(/* GraphQL */ `
      query ProducerPage(
        $collectionId: ID!
        $producerId: ID!
        $locale: ContentLocale
      ) {
        collection(collectionId: $collectionId, locale: $locale) {
          ...ProducerPage_Event
        }
      }
    `),
    {
      collectionId,
      producerId,
      locale,
    },
    { "bottlebooks-use-request-cache": "true" }
  );
  return result.collection;
}

export default function ProducerPageTemplate() {
  const { locale } = paramsSchema.parse(useParams());
  const data = useLoaderData() as LoaderData;
  const event = getFragment(fragment, data);
  const registeredBrands = event?.registeredBrands.nodes;

  if (!registeredBrands) return <div>No registered brands found.</div>;
  const registeredBrand = registeredBrands[0];

  return <ProducerPage event={event} data={registeredBrand} locale={locale} />;
}
